import React from 'react';
import classNames from 'classnames';
import { createStyles, WithStyles, withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { Link as LinkIcon } from '@material-ui/icons';
import Link from '@material-ui/core/Link';
import Chip from '@material-ui/core/Chip';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';

import { colors } from '../../themes/colors';

const styles = (theme) =>
  createStyles({
    dataRow: {
      display: 'flex',
      justifyContent: 'space-between',
      marginTop: 8,
      marginBottom: 8,
    },
    dataRowCompact: {
      marginTop: 0,
    },
    dataTitle: {
      color: colors.black2,
      fontFamily: 'Avenir-Book',
      fontSize: 14,
      textAlign: 'left',
    },
    dataSubtitle: {
      color: colors.black3,
      fontFamily: 'Avenir-Black',
      fontSize: 14,
      textAlign: 'left',
    },
    dataLink: {
      display: 'flex',
      alignItems: 'center',
    },
    dataLinkFont: {
      color: colors.blue6,
      fontFamily: 'Avenir-Medium',
      fontSize: 14,
    },
    dataLinkText: {
      textDecoration: 'underline',
      marginLeft: 5,
    },
    dataChip: {
      marginRight: 5,
      marginTop: 3,
      marginBottom: 3,
      borderRadius: 3,
      backgroundColor: colors.blue7,
    },
    dataChipLabel: {
      color: colors.black2,
      fontFamily: 'Avenir-Medium',
      fontSize: 14,
    },
    dataValue: {
      color: colors.black3,
      fontFamily: 'Avenir-Medium',
      fontSize: 14,
      textAlign: 'right',
    },
    boldValue: {
      color: colors.black3,
      fontFamily: 'Avenir-Black',
      fontSize: 14,
    },
    redValue: {
      color: colors.red1,
      fontFamily: 'Avenir-Black',
      fontSize: 14,
    },
    dataListRoot: {
      width: '100%',
    },
    dataListItem: {
      display: 'flex',
      justifyContent: 'space-between',
      paddingLeft: 0,
      paddingRight: 0,
    },
    dataListItemCompact: {
      paddingTop: 0,
    },
    dataListRowFirst: {
      display: 'flex',
      alignItems: 'center',
    },
    dataListRowFirstValue: {
      marginRight: 15,
    },
  });

type MemberSettingsListProps = WithStyles<typeof styles> & {
  list?: any;
  compact?: boolean;
};

const MemberSettingsList: React.FC<MemberSettingsListProps> = ({ list, compact, classes }) => {
  return (
    <List disablePadding className={classes.dataListRoot}>
      {list.map((dataItem, index) => (
        <ListItem
          className={classNames(classes.dataListItem, { [classes.dataListItemCompact]: compact })}
          key={index}
        >
          <Typography className={classes.dataTitle}>
            {[
              dataItem.key,
              ...(dataItem.extraKey
                ? [<br key="separator" />, <em key="extra">{dataItem.extraKey}</em>]
                : []),
            ]}
          </Typography>
          <Typography className={classes.dataValue}>{dataItem.value}</Typography>
        </ListItem>
      ))}
    </List>
  );
};

type MemberProfileRowProps = WithStyles<typeof styles> & {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  link?: string;
  linkText?: string;
  chips?: any;
  value?: any;
  list?: any;
  boldValue?: boolean;
  redValue?: boolean;
  compact?: boolean;
};

const MemberProfileRow: React.FC<MemberProfileRowProps> = ({
  title,
  subtitle,
  link,
  linkText,
  chips,
  value,
  list,
  boldValue = false,
  redValue = false,
  compact = false,
  classes,
}) => {
  return (
    <div className={classNames(classes.dataRow, { [classes.dataRowCompact]: compact })}>
      {title && <Typography className={classes.dataTitle}>{title}</Typography>}
      {subtitle && (
        <>
          {React.isValidElement(subtitle) ? (
            subtitle
          ) : (
            <Typography className={classes.dataSubtitle}>{subtitle}</Typography>
          )}
        </>
      )}
      {link && (
        <Link href={link} className={classes.dataLink}>
          <LinkIcon className={classes.dataLinkFont} />
          <Typography className={classNames(classes.dataLinkFont, classes.dataLinkText)}>
            {linkText}
          </Typography>
        </Link>
      )}
      {chips && (
        <div>
          {chips.map((chipName, index) => {
            return (
              <Chip
                className={classes.dataChip}
                classes={{ label: classes.dataChipLabel }}
                label={chipName}
                key={index}
              />
            );
          })}
        </div>
      )}
      {value && (
        <Typography
          className={classNames(classes.dataValue, {
            [classes.boldValue]: boldValue,
            [classes.redValue]: redValue,
          })}
        >
          {value}
        </Typography>
      )}
      {list && <MemberSettingsList list={list} classes={classes} compact={compact} />}
    </div>
  );
};

export default withStyles(styles)(MemberProfileRow);
